import { useCallback, useEffect, useState } from "react";
import { getWebPushConfig, saveWebPushSubscription } from "services/webPushService";

const toUint8Array = (value) => {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const raw = window.atob((value + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
};
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const getBrowserContext = () => ({
  userAgent: navigator.userAgent,
  displayMode: isStandalone() ? "standalone" : "browser",
  capturedAt: window.__livrestoInstallPromptCapturedAt || null,
  visibility: document.visibilityState,
});
const logInstallError = (stage, error, context = {}) => {
  console.error("[PWA Install]", { stage, message: error?.message || String(error), ...context });
};

export default function usePosNotifications() {
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [permission, setPermission] = useState(supported ? Notification.permission : "unsupported");
  const [subscribed, setSubscribed] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [installPrompt, setInstallPrompt] = useState(window.__livrestoInstallPrompt || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [installError, setInstallError] = useState("");
  const [installState, setInstallState] = useState(installed ? "installed" : "checking");
  const [installChecks, setInstallChecks] = useState({
    secure: window.isSecureContext,
    manifest: false,
    serviceWorker: false,
    browserReady: Boolean(window.__livrestoInstallPrompt),
  });

  const checkInstallReadiness = useCallback(async () => {
    if (isStandalone()) {
      setInstalled(true); setInstallState("installed"); return;
    }
    setInstallState("checking"); setInstallError("");
    const next = { secure: window.isSecureContext, manifest: false, serviceWorker: false, browserReady: Boolean(window.__livrestoInstallPrompt) };
    try {
      const manifestResponse = await fetch(`/manifest.json?v=${Date.now()}`, { cache: "no-store" });
      const manifest = manifestResponse.ok ? await manifestResponse.json() : null;
      const requiredIcons = (manifest?.icons || []).filter((icon) => icon.sizes === "192x192" || icon.sizes === "512x512");
      const iconResponses = await Promise.all(requiredIcons.map((icon) => fetch(new URL(icon.src, window.location.origin), { cache: "no-store" })));
      next.manifest = requiredIcons.length >= 2 && iconResponses.every((response) => response.ok);
      if (!next.manifest) {
        logInstallError("manifest_validation", new Error("Required PWA manifest or icons are unavailable"), {
          manifestStatus: manifestResponse.status,
          requiredIconCount: requiredIcons.length,
          iconStatuses: iconResponses.map((response) => response.status),
        });
      }
    } catch (diagnosticError) {
      next.manifest = false;
      logInstallError("manifest_and_icons", diagnosticError);
    }
    if ("serviceWorker" in navigator) {
      try {
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => window.setTimeout(() => reject(new Error("timeout")), 8000)),
        ]);
        next.serviceWorker = Boolean(registration?.active);
      } catch (workerError) {
        next.serviceWorker = false;
        logInstallError("service_worker_ready", workerError, { controller: Boolean(navigator.serviceWorker.controller) });
      }
    } else {
      logInstallError("service_worker_support", new Error("Service Worker API is unavailable"), { secureContext: window.isSecureContext });
    }
    next.browserReady = Boolean(window.__livrestoInstallPrompt);
    setInstallPrompt(window.__livrestoInstallPrompt || null);
    setInstallChecks(next);
    setInstallState(next.browserReady ? "ready" : "waiting");
    if (!next.browserReady) {
      console.warn("[PWA Install] browser prompt unavailable", { ...next, ...getBrowserContext() });
    }
  }, []);

  useEffect(() => {
    let heartbeat;
    if (supported) {
      navigator.serviceWorker.ready.then((registration) => registration.pushManager.getSubscription())
        .then(async (subscription) => {
          setSubscribed(Boolean(subscription));
          if (subscription && Notification.permission === "granted") {
            await saveWebPushSubscription(subscription);
            heartbeat = window.setInterval(() => saveWebPushSubscription(subscription).catch(() => {}), 30 * 60 * 1000);
          }
        }).catch(() => {});
    }
    checkInstallReadiness();
    const onInstallReady = (event) => {
      console.info("[PWA Install] native prompt ready");
      setInstallPrompt(event.detail);
      setInstallChecks((current) => ({ ...current, browserReady: true }));
      setInstallState("ready"); setInstallError("");
    };
    const onInstalled = () => {
      console.info("[PWA Install] appinstalled event received");
      setInstalled(true); setInstallPrompt(null); setInstallState("installed");
    };
    window.addEventListener("livresto:pwa-install-ready", onInstallReady);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("livresto:pwa-install-ready", onInstallReady);
      window.removeEventListener("appinstalled", onInstalled);
      if (heartbeat) window.clearInterval(heartbeat);
    };
  }, [supported, checkInstallReadiness]);

  const enableNotifications = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") {
        setError(result === "denied" ? "Les notifications sont bloquées dans le navigateur. Autorisez-les depuis l’icône à gauche de l’adresse du site." : "L’autorisation est nécessaire pour recevoir les commandes hors de l’écran Livresto.");
        return false;
      }
      const [{ public_key: publicKey }, registration] = await Promise.all([getWebPushConfig(), navigator.serviceWorker.ready]);
      if (!publicKey) throw new Error("La clé Web Push Livresto n’est pas configurée sur le serveur.");
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toUint8Array(publicKey) });
      await saveWebPushSubscription(subscription);
      setSubscribed(true);
      registration.showNotification("Notifications Livresto activées", {
        body: "Ce poste Windows peut maintenant vous avertir lorsqu’une commande arrive.",
        icon: "/livresto-partner-192.png", badge: "/favicon-32.png", tag: "livresto-setup-complete",
      });
      return true;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || "Activation impossible.");
      return false;
    } finally { setBusy(false); }
  }, []);

  const install = useCallback(async () => {
    setInstallError("");
    let prompt = installPrompt || window.__livrestoInstallPrompt;
    if (!prompt) {
      await checkInstallReadiness();
      prompt = window.__livrestoInstallPrompt;
    }
    if (!prompt) {
      if (!window.isSecureContext) {
        setInstallError("L’installation nécessite HTTPS. Ouvrez l’adresse sécurisée officielle de Livresto.");
      } else {
        setInstallError("Livresto est prêt, mais Windows n’a pas proposé son dialogue d’installation. Restez 30 secondes sur cette page et cliquez une fois dans le dashboard. Si le bouton reste indisponible, utilisez l’icône Installer dans la barre d’adresse de Microsoft Edge ou Chrome.");
      }
      logInstallError("native_prompt_unavailable", new Error("beforeinstallprompt was not emitted"), { ...installChecks, ...getBrowserContext() });
      return false;
    }
    try {
      setInstallState("prompting");
      await prompt.prompt();
      const choice = await prompt.userChoice;
      console.info("[PWA Install] native prompt result", { outcome: choice.outcome, platform: choice.platform });
      if (choice.outcome === "accepted") {
        setInstallState("installing");
        window.setTimeout(() => { setInstalled(true); setInstallState("installed"); }, 1200);
        setInstallPrompt(null);
        window.__livrestoInstallPrompt = null;
        return true;
      }
      setInstallState("ready");
      setInstallError("L’installation a été annulée. Cliquez de nouveau quand vous serez prêt.");
      return false;
    } catch (promptError) {
      logInstallError("native_prompt", promptError, installChecks);
      setInstallState("waiting");
      setInstallError(`Windows n’a pas pu ouvrir l’installation : ${promptError?.message || "erreur inconnue"}`);
      return false;
    }
  }, [installPrompt, checkInstallReadiness, installChecks]);

  const appChecks = [installChecks.secure, installChecks.manifest, installChecks.serviceWorker];
  const installProgress = Math.round((appChecks.filter(Boolean).length / appChecks.length) * 100);
  return { supported, permission, subscribed, installed, canInstall: Boolean(installPrompt), busy, error,
    installError, installState, installChecks, installProgress, enableNotifications, install, checkInstallReadiness };
}
